import { css } from 'aphrodite';
import { FC, useState } from 'react';
import { Dropdown, DropdownContent, DropdownItem, DropdownSelectTrigger } from '../../../../web/components-v2/Dropdown';
import { bs } from '../../../../web/styles/styles';
import { Column, Row } from '../../../components/Layout';
import { InfoPopover } from '../../../components/InfoPopover/InfoPopover';
import { styleSheet } from '../../../components/blogs/styles';
import { getAllPresets, StylePreset } from './presets';
import { PresetPreviewModal } from './PresetPreviewModal';

interface PresetSelectorProps {
	currentPresetId: string;
	onPresetSelect: (preset: StylePreset) => void;
	disabled?: boolean;
	hasUnsavedChanges?: boolean;
}

export const PresetSelector: FC<PresetSelectorProps> = ({
	currentPresetId,
	onPresetSelect,
	disabled,
	hasUnsavedChanges,
}) => {
	const [previewPreset, setPreviewPreset] = useState<StylePreset | null>(null);
	const [dropdownOpen, setDropdownOpen] = useState(false);

	const allPresets = getAllPresets();
	const currentPreset = allPresets.find(p => p.id === currentPresetId);

	const handlePresetClick = (preset: StylePreset) => {
		if (preset.id === 'custom') {
			return;
		}
		setPreviewPreset(preset);
		setDropdownOpen(false);
	};

	const handleApplyPreset = (_: never, canceled: boolean) => {
		if (!canceled && previewPreset) {
			onPresetSelect(previewPreset);
		}
		setPreviewPreset(null);
	};

	return (
		<>
			<Row className={css(bs.itemsStart, bs.gap4, bs.mb6, bs.p4, bs.bgGray50, bs.rounded)}>
				<div className={css(bs.flex, bs.flexCol, bs.flex1)}>
					<Row className={css(bs.itemsCenter, bs.gap2, bs.mb2)}>
						<label className={css(styleSheet.label, bs.mb0)}>Style Preset</label>
						<InfoPopover message='Apply a pre-built style preset to quickly customize your blog appearance. You can further customize any preset after applying it.' />
					</Row>
					<p className={css(bs.textSm, bs.textGray600)}>
						Choose from professional templates or create your own custom style
					</p>
				</div>

				<div className={css(bs.flex, bs.flexCol, bs.pt1)}>
					<Dropdown open={dropdownOpen} onOpenChange={setDropdownOpen}>
						<DropdownSelectTrigger disabled={disabled}>
							<span className={css(bs.truncateText)}>
								{currentPreset?.name || 'Select a preset'}
								{currentPresetId === 'custom' && hasUnsavedChanges && ' *'}
							</span>
						</DropdownSelectTrigger>

						<DropdownContent align='start'>
							<div className={css(bs.p2, bs.textXs, bs.textGray600, bs.uppercase)}>Pre-built Presets</div>
							{allPresets
								.filter(p => p.id !== 'custom')
								.map(preset => (
									<DropdownItem
										key={preset.id}
										onClick={() => handlePresetClick(preset)}
										styles={[currentPresetId === preset.id && bs.bgGray200]}
									>
										<Column className={css(bs.gap1)}>
											<Row className={css(bs.itemsCenter, bs.justifyBetween)}>
												<span className={css()}>{preset.name}</span>
												{currentPresetId === preset.id && (
													<span className={css(bs.textXs, bs.textGray600)}>(Current)</span>
												)}
											</Row>
											<span className={css(bs.textXs, bs.textGray600)}>{preset.description}</span>
										</Column>
									</DropdownItem>
								))}

							{currentPresetId === 'custom' && (
								<>
									<div className={css(bs.borderT, bs.my3)} />
									<div className={css(bs.p2, bs.textXs, bs.textGray600, bs.uppercase)}>Your Style</div>
									<DropdownItem styles={[bs.bgGray200]}>
										<Column className={css(bs.gap1)}>
											<Row className={css(bs.itemsCenter, bs.justifyBetween)}>
												<span className={css()}>Custom</span>
												<span className={css(bs.textXs, bs.textGray600)}>(Current)</span>
											</Row>
											<span className={css(bs.textXs, bs.textGray600)}>Your custom settings</span>
										</Column>
									</DropdownItem>
								</>
							)}
						</DropdownContent>
					</Dropdown>

					{currentPresetId === 'custom' && hasUnsavedChanges && (
						<p className={css(bs.textXs, bs.textGray600, bs.mt1)}>* Unsaved changes</p>
					)}
				</div>
			</Row>

			{previewPreset && (
				<PresetPreviewModal
					preset={previewPreset}
					modalProps={{
						isOpen: !!previewPreset,
						onRequestClose: handleApplyPreset,
					}}
					hasUnsavedChanges={hasUnsavedChanges}
				/>
			)}
		</>
	);
};
